/**
 * Clocking the team in and out from one place.
 *
 * "I also need to be able to clock ppl in and out owner and manager then staff
 *  are able to do themselves. I'd also allow front desk to Handel it as well
 *  click all in/out"
 *
 * One row per person with the button that applies to them, so whoever is at
 * the desk at opening can put everybody on without each of them finding a
 * screen. People already on are listed first, because at the end of the day
 * that is the list you are working down.
 *
 * Anybody can see it. Only an owner, manager or the front desk can press
 * anything on somebody else's row — the server decides that and says so, and
 * this just hides buttons that would be refused.
 *
 * "Can we collapse that because I have a lot of other employees I just started
 *  with those but it will take up space and we need to see the clients"
 *
 * So it folds to one line: who is on, and the end-of-day button. On the front
 * desk it starts folded, because the clients are what that page is for; on
 * Team, where clocking people is the job, it starts open. Whichever she leaves
 * it at is what she gets next time.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronDown, LogIn, LogOut, Users } from "lucide-react";
import { Button, useConfirm, useToast } from "@/components/ui";
import { Avatar } from "@/components/ui/Avatar";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { hoursLabel } from "./TimeClock";

interface RosterPerson {
  id: string;
  name: string;
  staff_role: string;
  color: string | null;
  credential: string | null;
  on: boolean;
  started_at: string | null;
  hours: number;
}
interface Roster {
  can_clock_others: boolean;
  me: string;
  staff: RosterPerson[];
}

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const FOLD_KEY = "hh.roster.open";

export function ClockRoster({ defaultOpen = true }: { defaultOpen?: boolean }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [data, setData] = useState<Roster | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState(() => {
    // A remembered choice beats the page's default. Storage can be unavailable.
    try {
      const saved = localStorage.getItem(FOLD_KEY);
      if (saved === "1" || saved === "0") return saved === "1";
    } catch { /* private window, blocked storage */ }
    return defaultOpen;
  });
  const setOpenPersisted = (next: boolean) => {
    setOpen(next);
    try { localStorage.setItem(FOLD_KEY, next ? "1" : "0"); } catch { /* ignore */ }
  };

  const load = useCallback(() => {
    api.get<Roster>("/staff/clock/roster").then(setData).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (p: RosterPerson) => {
    setBusy(p.id);
    try {
      await api.post(`/staff/clock/${p.on ? "out" : "in"}`, { user_id: p.id });
      toast.success(p.on ? `${p.name} clocked out` : `${p.name} clocked in`);
      load();
    } catch (e) {
      // "They're already clocked in" is worth showing as-is: it means somebody
      // else got there first, and reloading tells the truth.
      toast.error(e instanceof ApiError ? e.message : "That didn't work");
      load();
    } finally { setBusy(null); }
  };

  const allOut = async () => {
    const on = data?.staff.filter((p) => p.on) ?? [];
    const ok = await confirm({
      title: `Clock out ${on.length === 1 ? "1 person" : `all ${on.length} people`}?`,
      description: on.map((p) => p.name).join(", "),
      confirmLabel: "Clock them out",
    });
    if (!ok) return;
    setBusy("all");
    try {
      const r = await api.post<{ clocked_out: number }>("/staff/clock/out-all", {});
      toast.success(`${r.clocked_out} clocked out`);
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "That didn't work");
    } finally { setBusy(null); }
  };

  if (!data) return null;
  const on = data.staff.filter((p) => p.on);

  return (
    <div data-clock-roster className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          data-roster-fold
          aria-expanded={open}
          onClick={() => setOpenPersisted(!open)}
          className="flex min-w-0 items-center gap-1.5 rounded text-[11px] font-semibold uppercase tracking-wide text-zinc-500 hover:text-zinc-300"
        >
          <Users className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            {on.length === 0 ? "Nobody is on" : on.length === 1 ? "1 person is on" : `${on.length} people are on`}
          </span>
          {/* Folded, the names are the whole point of the line. */}
          {!open && on.length > 0 && (
            <span className="truncate font-normal normal-case tracking-normal text-zinc-600">
              {on.map((p) => p.name.split(" ")[0]).join(", ")}
            </span>
          )}
          <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-180")} />
        </button>
        {data.can_clock_others && on.length > 0 && (
          <Button variant="ghost" size="sm" disabled={busy !== null} onClick={() => void allOut()}>
            Clock everyone out
          </Button>
        )}
      </div>

      {open && (
      <ul className="divide-y divide-zinc-800/70 overflow-hidden rounded-xl border border-zinc-800">
        {data.staff.map((p) => {
          // Your own row is always actionable — that is the original clock.
          const mine = p.id === data.me;
          const canPress = mine || data.can_clock_others;
          return (
            <li
              key={p.id}
              data-roster-row
              className={cn("flex items-center gap-2.5 px-3 py-2", p.on && "bg-emerald-500/5")}
            >
              <Avatar name={p.name} color={p.color} size="xs" />
              <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">
                {p.name}
                {p.credential && <span className="ml-1 text-zinc-500">{p.credential}</span>}
                {mine && <span className="ml-1.5 text-[11px] text-zinc-600">(you)</span>}
              </span>
              {p.on && p.started_at && (
                <span className="shrink-0 text-[11px] tabular-nums text-zinc-500">
                  since {timeOf(p.started_at)} · {hoursLabel(p.hours)}
                </span>
              )}
              {canPress ? (
                <Button
                  size="sm"
                  variant={p.on ? "secondary" : "primary"}
                  disabled={busy !== null}
                  leftIcon={p.on ? <LogOut className="h-3.5 w-3.5" /> : <LogIn className="h-3.5 w-3.5" />}
                  onClick={() => void toggle(p)}
                >
                  {p.on ? "Out" : "In"}
                </Button>
              ) : (
                <span className="text-[11px] text-zinc-600">{p.on ? "On" : "Off"}</span>
              )}
            </li>
          );
        })}
      </ul>
      )}
    </div>
  );
}
